import { List } from 'antd';
import styled, { useTheme } from 'styled-components';
import useTestAddClientSelectListController from '../../../1_application/test/testAddClientSelectListController';
import { ClientSummary } from '../../../2_domain/client/client';
import toIDString from '../../../lib/util/dateFormatter';
import { getIndex } from '../../../lib/util/pagination';
import { SearchControlBoxContainer } from '../../core/components/box/boxContainer';
import Column from '../../core/components/box/column';
import Row from '../../core/components/box/row';
import SizedBox from '../../core/components/box/sizedBox';
import LXMiddleButton from '../../core/components/buttons/lxMiddleButton';
import LXList from '../../core/components/list/lx_list';
import LXSearcher from '../../core/texts/lxSearcher';
import { LXbody, LXCaption, LXh4 } from '../../core/texts/lxText';
import LXPagination from '../../core/pagination/lxPagination';
import LXPageSizeSelector from '../../core/components/buttons/lxPageSizeSelector';
import { ReactComponent as On } from '../../../assets/icons/radioOn.svg';
import { ReactComponent as Off } from '../../../assets/icons/radioOff.svg';

interface TestAddClientSelectListProps {
  client?: ClientSummary;
  onClientSelect: (newClient: ClientSummary) => void;
}

const HeaderPadding = styled.div`
  padding: 0px 10px 0px 56px;
`;

const ItemPadding = styled.div`
  padding: 20px 10px 20px 16px;
  cursor: pointer;

  &:hover {
    background-color: ${(props) => props.theme.colors.primaryBg};
  }
`;

const TestAddClientSelectList = (props: TestAddClientSelectListProps) => {
  const theme = useTheme();
  const c = useTestAddClientSelectListController();

  const header = (
    <Row justifyContent="space-between">
      <Column>
        <LXh4>{theme.texts.selectClient}</LXh4>
        <SizedBox height={4}></SizedBox>
        <LXbody color={theme.colors.caption}>
          {theme.texts.pleaseSelectAClientToBeTested}
        </LXbody>
      </Column>
      <LXMiddleButton onClick={c.addNewClientHandler}>
        {theme.texts.plusAddNew}
      </LXMiddleButton>
    </Row>
  );

  const listHeader = (
    <HeaderPadding>
      <Row alignItems="center">
        <LXCaption width={'24px'}>{theme.texts.no}</LXCaption>
        <SizedBox width={16}></SizedBox>
        <LXCaption width={'108px'}>{theme.texts.clientNo}</LXCaption>
        <SizedBox width={16}></SizedBox>
        <LXCaption width={'190px'}>{theme.texts.name}</LXCaption>
        <SizedBox width={16}></SizedBox>
        <LXCaption width={'134px'}>{theme.texts.dateOfBirth}</LXCaption>
        <SizedBox width={16}></SizedBox>
        <LXCaption width={'50px'}>{theme.texts.gender}</LXCaption>
      </Row>
    </HeaderPadding>
  );

  const listItemBuilder = (client: ClientSummary, idx: number) => {
    return (
      <List.Item>
        <ItemPadding onClick={() => props.onClientSelect(client)}>
          <Row alignItems="center">
            {client.id === props.client?.id ? <On></On> : <Off></Off>}
            <SizedBox width={24}></SizedBox>
            <LXbody width={'24px'}>
              {getIndex(c.pageResult.pageNumber, c.pagination.pageSize, idx)}
            </LXbody>
            <SizedBox width={16}></SizedBox>
            <LXbody width={'108px'}>{client.clientSerial}</LXbody>
            <SizedBox width={16}></SizedBox>
            <LXbody width={'190px'}>{client.name}</LXbody>
            <SizedBox width={16}></SizedBox>
            <LXbody width={'134px'}>{toIDString(client.birthday)}</LXbody>
            <SizedBox width={16}></SizedBox>
            <LXbody width={'50px'}>
              {client.gender === 'MALE' ? theme.texts.male : theme.texts.female}
            </LXbody>
          </Row>
        </ItemPadding>
      </List.Item>
    );
  };

  return (
    <Column>
      {header}
      <SizedBox height={30}></SizedBox>
      <SearchControlBoxContainer width="none">
        <LXSearcher
          onChange={c.setSearchText}
          width={320}
          placeholder={theme.texts.keywordSearch}
        ></LXSearcher>
      </SearchControlBoxContainer>
      <SizedBox height={40}></SizedBox>
      <div className="ant-list-no-padding">
        <LXList<ClientSummary>
          datas={c.clients}
          header={listHeader}
          itemBuilder={listItemBuilder}
          loading={c.isLoading}
        ></LXList>
      </div>
      <Row justifyContent="space-between" alignItems="center">
        <div className="ant-pagination-item-background-color-white">
          <LXPagination
            pageSize={c.pagination.pageSize}
            current={c.pageResult?.pageNumber ?? 0}
            onChange={c.setPageNumber}
            total={c.pageResult?.totalElements ?? 0}
          ></LXPagination>
        </div>

        <Row alignItems="center">
          <LXbody color={theme.colors.caption}>
            {theme.texts.rowsPerPage}
          </LXbody>
          <SizedBox width={10}></SizedBox>
          <LXPageSizeSelector
            value={c.pagination.pageSize}
            onSelect={c.setPageSize}
          ></LXPageSizeSelector>
        </Row>
      </Row>
    </Column>
  );
};

export default TestAddClientSelectList;
